/**
 * PreferencesPopout — mobile chrome for global preferences.
 *
 * The desktop PreferencesBar shows an inline pill cluster (theme + mute)
 * with text labels — that works above 768px. Below 768px the labels crowd
 * the content surface, so we collapse to a single 44px gear-icon trigger
 * at top-right and open a designed cream-paper popout panel when tapped.
 *
 * This is a separate component from <PreferencesBar /> (approach B from
 * the brief): mounting both and gating with `useIsMobileChrome()` keeps
 * the desktop pattern fully untouched, gives the popout its own focus-
 * management hooks, and lets each presentation pick the right markup.
 *
 * Material:
 *   - Cream-paper card matching the variant cards' shadow vocabulary
 *     (multi-layer warm shadows, inset paper highlight, backdrop blur on
 *     the surface behind the popout).
 *   - Senior-preferred 56px row height inside the panel.
 *   - 280px panel width on phone, 320px on slightly wider mobile.
 *
 * Motion:
 *   - Spring-physics reveal from the trigger's top-right corner
 *     (stiffness 280, damping 24, mass 0.9) — feels like a thing, not a
 *     dropdown menu.
 *   - prefers-reduced-motion: pure opacity fade, no scale.
 *
 * Accessibility:
 *   - aria-expanded on the trigger reflects open state.
 *   - First focusable element inside the panel receives focus on open;
 *     focus returns to the trigger on close.
 *   - Escape key closes the panel.
 *   - Tap on the backdrop closes the panel.
 *   - Inner controls use aria-pressed for active state.
 *   - All controls carry full text labels (icons are aria-hidden).
 */
import { AnimatePresence, motion } from 'motion/react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { prefersReducedMotion } from '@/juice/timings';

import { playChromeSound } from './chromeSound';
import { useIsMobileChrome } from './useIsMobileChrome';
import { usePreferences } from './usePreferences';

import './PreferencesPopout.css';

const POPOUT_ID = 'preferences-popout-panel';

export function PreferencesPopout() {
  const isMobile = useIsMobileChrome();
  const { theme, setTheme, muted, toggleMute } = usePreferences();
  const [open, setOpen] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);

  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const firstFocusableRef = useRef<HTMLButtonElement | null>(null);

  // Track prefers-reduced-motion live (not just on mount) — kiosk users
  // can flip the OS setting between play sessions.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = (): void => setReducedMotion(mql.matches);
    update();
    mql.addEventListener('change', update);
    return () => mql.removeEventListener('change', update);
  }, []);

  // Close if the viewport jumps above the mobile threshold while open
  // (otherwise the popout sits orphaned on top of the desktop bar).
  useEffect(() => {
    if (!isMobile && open) {
      setOpen(false);
    }
  }, [isMobile, open]);

  // Focus management — on open, move focus into the panel; on close,
  // return focus to the trigger.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      // Wait one frame so the panel exists in the DOM before focusing.
      const id = requestAnimationFrame(() => {
        firstFocusableRef.current?.focus();
      });
      wasOpenRef.current = true;
      return () => cancelAnimationFrame(id);
    }
    if (!open && wasOpenRef.current) {
      triggerRef.current?.focus();
      wasOpenRef.current = false;
    }
    return undefined;
  }, [open]);

  // Escape closes.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
        playChromeSound('popout-close');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const handleTriggerClick = useCallback(() => {
    setOpen((wasOpen) => {
      playChromeSound(wasOpen ? 'popout-close' : 'popout-open');
      return !wasOpen;
    });
  }, []);

  const handleBackdropClick = useCallback(() => {
    setOpen(false);
    playChromeSound('popout-close');
  }, []);

  const handleSelectTheme = useCallback(
    (next: 'light' | 'dark') => {
      if (next !== theme) setTheme(next);
      playChromeSound('row-tap');
    },
    [theme, setTheme],
  );

  const handleToggleMute = useCallback(() => {
    toggleMute();
    playChromeSound('row-tap');
  }, [toggleMute]);

  if (!isMobile) return null;

  const initial = reducedMotion ? { opacity: 0 } : { opacity: 0, scale: 0.85 };
  const animate = reducedMotion ? { opacity: 1 } : { opacity: 1, scale: 1 };
  const exit = reducedMotion ? { opacity: 0 } : { opacity: 0, scale: 0.9 };
  const transition = reducedMotion
    ? { duration: 0.15 }
    : { type: 'spring' as const, stiffness: 280, damping: 24, mass: 0.9 };

  return (
    <div className="prefs-popout-root">
      <motion.button
        ref={triggerRef}
        type="button"
        className={`prefs-popout-trigger${open ? ' is-open' : ''}`}
        onClick={handleTriggerClick}
        aria-expanded={open}
        aria-controls={POPOUT_ID}
        aria-label="Open settings"
        {...(reducedMotion ? {} : { whileTap: { scale: 0.94 } })}
        transition={
          reducedMotion ? { duration: 0 } : { type: 'spring', stiffness: 500, damping: 28 }
        }
      >
        <GearIcon />
      </motion.button>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              key="backdrop"
              className="prefs-popout-backdrop"
              onClick={handleBackdropClick}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: reducedMotion ? 0.15 : 0.22 }}
              aria-hidden="true"
            />
            <motion.div
              ref={panelRef}
              id={POPOUT_ID}
              key="panel"
              role="dialog"
              aria-label="Settings"
              aria-modal="false"
              className="prefs-popout-panel"
              initial={initial}
              animate={animate}
              exit={{ ...exit, transition: { duration: reducedMotion ? 0.12 : 0.18 } }}
              transition={transition}
              style={{ transformOrigin: 'top right' }}
            >
              <div className="prefs-popout-panel__heading">Settings</div>

              <div
                className="prefs-popout-section"
                role="group"
                aria-label="Room light"
              >
                <div className="prefs-popout-section__label">Room light</div>
                <div className="prefs-popout-section__row">
                  <button
                    ref={firstFocusableRef}
                    type="button"
                    className={`prefs-popout-option${theme === 'light' ? ' is-on' : ''}`}
                    onClick={() => handleSelectTheme('light')}
                    aria-pressed={theme === 'light'}
                  >
                    <SunIcon />
                    <span>Daylight</span>
                  </button>
                  <button
                    type="button"
                    className={`prefs-popout-option${theme === 'dark' ? ' is-on' : ''}`}
                    onClick={() => handleSelectTheme('dark')}
                    aria-pressed={theme === 'dark'}
                  >
                    <MoonIcon />
                    <span>Lamplight</span>
                  </button>
                </div>
              </div>

              <div
                className="prefs-popout-section"
                role="group"
                aria-label="Sound"
              >
                <div className="prefs-popout-section__label">Sound</div>
                <button
                  type="button"
                  className={`prefs-popout-mute${muted ? ' is-muted' : ''}`}
                  onClick={handleToggleMute}
                  aria-pressed={muted}
                >
                  {muted ? <SpeakerMutedIcon /> : <SpeakerIcon />}
                  <span className="prefs-popout-mute__label">
                    {muted ? 'Muted' : 'Sound on'}
                  </span>
                  <span className="prefs-popout-mute__hint" aria-hidden="true">
                    {muted ? 'Tap to turn on' : 'Tap to mute'}
                  </span>
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

// Default reduced-motion read for the popout when used outside React.
// Kept here so the import isn't dead code if the hook is dropped later.
export function _isReducedMotion(): boolean {
  return prefersReducedMotion();
}

// ---------------------------------------------------------------------------
// Icons — structural UI primitives, not depictive imagery.
// ---------------------------------------------------------------------------

function GearIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path
        d="M19.5 12c0-.5-.05-1-.13-1.47l1.9-1.49-2-3.46-2.27.78a7.5 7.5 0 0 0-2.55-1.47L14 2.5h-4l-.45 2.39a7.5 7.5 0 0 0-2.55 1.47l-2.27-.78-2 3.46 1.9 1.49A7.5 7.5 0 0 0 4.5 12c0 .5.05 1 .13 1.47l-1.9 1.49 2 3.46 2.27-.78a7.5 7.5 0 0 0 2.55 1.47L10 21.5h4l.45-2.39a7.5 7.5 0 0 0 2.55-1.47l2.27.78 2-3.46-1.9-1.49c.08-.47.13-.97.13-1.47z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SunIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.8" />
      <path
        d="M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9l2 2M17.1 17.1l2 2M4.9 19.1l2-2M17.1 6.9l2-2"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M20 14.5A8.5 8.5 0 1 1 9.5 4a7 7 0 0 0 10.5 10.5z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SpeakerIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M4 9v6h4l5 4V5L8 9H4z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
        fill="none"
      />
      <path
        d="M16 8.5c1.2 1 2 2.3 2 3.5s-0.8 2.5-2 3.5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      <path
        d="M19 6c2 1.5 3 3.7 3 6s-1 4.5-3 6"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        opacity="0.55"
      />
    </svg>
  );
}

function SpeakerMutedIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M4 9v6h4l5 4V5L8 9H4z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
        fill="none"
      />
      <path
        d="M16 9l5 5M21 9l-5 5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}
